
import { createTheme } from "@mui/material"

export const color = {
    main: '#ee0033',
    a50: '#A50000',
    default: '#44494D',
    iconActive: '#C4132B',
    boxShadow: '#F7F7F7',
    grey01: '#606060',
    grey02: '#858585',
    grey03: '#8F9294',
    grey04: '#73777A',
    greyIL05: '#AAAAAA',
    greyIL06: '#E0E0E0',
    greyIL07: "#F0F0F0",
    white: '#FFFFFF',
    black: '#000000',
    darkGray: '#44494d',
    whiteF2: '#F2F2F2',
    RedP00: '#CB002B',
    darkRed: '#CB002B',
    redP01: '#ee0033',
    red00: '#AD0025',
    greenF02: '#00884F',
    orangeF06: '#FF6B00',
    backgroundColorChose: '#44494d14',
    hyperlink: '#AD0025',
    focus: '#EF929C',
    error: '#B3261E',
    placeHolder: '#73777A',
    secondary: '#A0616A',
    gray01: '#949494',
    orange: '#EE5F00',
    pink: '#FFDCDF',
    lightYellow: '#FFF3E2',
    yellow: '#A76700',
    yellow01: '#EEC73C',
    pink01: '#FE979E',
    pink02: '#F8EEF0'
}

export const colorT = {
    gray: color.darkGray,
    meetingGrid: {
        notStart: color.redP01,
        progress: color.redP01,
        ended: color.orange,
        canceled: color.gray01,
        expired: color.gray01
    },
    meeting: {
        notStart: color.white,
        progress: color.redP01,
        ended: color.orange,
        canceled: color.gray01,
        expired: color.gray01
    },
    meetingBackground: {
        expEnd: color.greyIL06,
        default: color.pink
    },
    meetingBorder: {
        left: '',
        right: color.pink01,
        default: color.redP01
    }
}

export const fontTypo = {
    title: "1.25rem",
    normal: "1rem"
}

const theme = createTheme({
    // UI - COLOR
    palette: {
        primary: {
            main: '#CB002B',
            dark: '#EE0033'
        },
        secondary: {
            main: '#A0616A'
        },
        focus: {
            main: '#EF929C'
        },
        text: {
            primary: '#44494D',
            dark: '#262626'
        },
        table: {
            head: '#F8EEF0'
        },
        ui: {
            hover: '#E9E9E9',
            disabled: '#E9E9E9'
        }
    }, 
    iconSize: {
        small: '24px',
        medium: '32px'
    },
    text: {
        main: '#44494D'
    },
    components: {
        MuiPagination: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '& div': {
                        borderRadius: '6px',
                        border: `1px solid ${color.greyIL05}`,
                        height: '32px'
                    },
                    '& .MuiButtonBase-root.MuiPaginationItem-root': {
                        borderRadius: '6px',
                        border: `1px solid ${color.greyIL05}`,
                        fontWeight: 500,
                        '&:hover': {
                            backgroundColor: theme.palette.ui.hover
                        }
                    },
                    '& .MuiButtonBase-root.MuiPaginationItem-root.Mui-selected': {
                        backgroundColor: theme.palette.primary.main,
                        color: color.white,
                        borderColor: theme.palette.primary.dark
                    }
                })
            }
        },
        MuiMenu: {
            styleOverrides: {
                root: {
                    '& .MuiPaper-root.MuiPopover-paper.MuiMenu-paper': {
                        overflow: 'hidden'
                    }
                }
            }
        },
        MuiBottomNavigationAction: {
            styleOverrides: {
                root: {
                    '& .Mui-selected': {
                        color: color.iconActive
                    }
                }
            }
        },
        MuiBottomNavigation: {
            styleOverrides: {
                root: {
                    height: '48px'
                }
            }
        },
        MuiToolbar: {
            styleOverrides: {
                root: {
                    minHeight: '48px'
                }
            }
        },
        MuiBackdrop: {
            styleOverrides: {
                root: {
                    // backgroundColor: 'rgba(68, 73, 77, 0.2)'
                }
            }
        },
        MuiRadio: {
            styleOverrides: {
                root: {
                    '&.Mui-checked': {
                        // color: color.RedP00  
                    }
                }
            }
        },
        MuiPopover: {
            styleOverrides: {
                root: {
                    '& .MuiPaper-root.MuiPopover-paper': {
                        overflow: 'initial',
                        marginTop: '0.75rem',
                        borderRadius: '16px'
                    }
                }
            }
        },
        MuiTableBody: {
            styleOverrides: {
                root: {
                    '& .MuiTableRow-root:nth-of-type(even)': {
                        backgroundColor: color.greyIL07
                    }
                }
            }
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    height: '48px',
                    '&.Mui-selected': {
                        backgroundColor: '#FFF5F6 !important'
                    }
                }
            }
        },
        MuiTableCell: {
            defaultProps: {
                size: 'small'
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    padding: '4px 12px',
                    fontSize: '15px',
                    fontWeight: 500,
                    color: color.black,
                    '&.MuiTableCell-head': {
                        borderTop: '1px solid #d1d1d1',
                        boxShadow: '0px 3px #d1d1d1',
                        fontWeight: 700,
                        backgroundColor: theme.palette.table.head,
                        borderLeft: '1px solid #E0E0E0',
                        borderBottom: 'none',
                        fontSize: '16px'
                    },
                    '& .MuiTypography-root': {
                        fontSize: '15px'
                    }
                })
            }
        },
        MuiTabs: {
            styleOverrides: {
                root: ({ theme}) => ({
                    minHeight: 'unset',
                    '& .MuiTabs-indicator': {
                        backgroundColor: theme.palette.primary.main,
                        bottom: '0.375em',
                    }
                })
            }
        },
        MuiTab: {
            styleOverrides: {
                root: ({ theme}) => ({
                    fontSize: '1.125em',
                    fontWeight: 700,
                    textTransform: 'unset',
                    padding: 0,
                    minHeight: 'unset',
                    height: '36px',
                    '&.Mui-selected': {
                        color: theme.palette.primary.main
                    },
                    '& + .MuiTab-root': {
                        marginLeft: '40px'
                    }
                })
            }
        },
        MuiButton: {
            defaultProps: {
                variant: 'contained',
                size: 'small'
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    fontWeight: 600,
                    textTransform: 'initial',
                    fontSize: '1em',
                    textWrap: 'nowrap',
                    borderRadius: '6px',
                    '&.MuiButton-contained': {
                        color: '#fff',
                        padding: '0.25em 1em',
                        fontWeight: '700',
                        backgroundColor: theme.palette.primary.main,
                        '&:hover': {
                            backgroundColor: theme.palette.primary.main
                        },
                        '&:disabled': {
                            backgroundColor: '#E0E0E0 !important',
                            color: '#8F9294',
                            fontWeight: '700',
                            boxShadow: 'none'
                        },
                        '&.MuiLoadingButton-root': {
                            backgroundColor: theme.palette.primary.main,
                        },
                    },
                    '&.MuiButton-outlined': {
                        padding: '3px 1em',
                        color: theme.palette.secondary.main,
                        borderColor: theme.palette.secondary.main,
                        fontWeight: '700',
                        fontSize: '16px',
                        lineHeight: '28px',
                        '&:hover': {
                            backgroundColor: '#EE00330A'
                        },
                        '&:disabled': {
                            backgroundColor: '#FFFFFF',
                            color: '#8F9294',
                            borderColor: '#E0E0E0',
                            '& .MuiButton-startIcon': {
                                '& svg path': {
                                    fill: '#8F9294'
                                },
                            }
                        },
                        '&.btn-greyIL05': { // Style cho btn design mới
                            borderColor: color.greyIL05,
                            color: color.black,
                            backgroundColor: color.white,
                            '&:hover': {
                                backgroundColor: color.white,
                                borderColor: theme.palette.focus.main
                            },
                            '&[aria-expanded="true"]': {
                                borderColor: theme.palette.focus.main
                            }
                        },
                        '&.MuiLoadingButton-root': {
                            backgroundColor: theme.palette.primary.main,
                            '&:disabled': {
                                backgroundColor: '#FFFF !important',
                                color: '#8F9294 !important',
                                borderColor: '#E0E0E0 !important'
                            }
                        },
                        '& .MuiButton-startIcon': {
                            '& svg path': {
                                fill: theme.palette.secondary.main
                            },
                        },
                        '& .MuiButton-endIcon': {
                            '& svg path': {
                                fill: theme.palette.secondary.main
                            },
                        }
                    },
                    '&.MuiButton-text': {
                        '&:hover': {
                            backgroundColor: 'rgb(68 73 77 / 22%)'
                        }
                    },
                    '&.MuiPickersDay-root.Mui-selected': {
                        backgroundColor: 'red'
                    },
                    '& .MuiButton-startIcon': {
                        [theme.breakpoints.up("md")]: {
                            marginRight: '8px'
                        },
                        [theme.breakpoints.down("md")]: {
                            marginRight: '0px'
                        },
                    }
                })
            }
        },
        MuiIconButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '&:hover': {
                        backgroundColor: 'rgb(68 73 77 / 22%)',
                        opacity: 0.9
                    }
                })
            }
        },
        MuiTextField: {
            defaultProps: {
                size: 'small'
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    variants: [
                        {
                            props: { fullWidth: true },
                            style: {
                                width: '100%'
                            },
                        }
                    ],
                    width: '256px',

                    "& .MuiInputBase-input": {
                        paddingTop: "6.5px",
                        paddingBottom: "6.5px"
                    },
                    "& .MuiInputAdornment-root.MuiInputAdornment-positionStart:has(button)": {
                        marginLeft: '-8px',
                        marginRight: '4px'
                    },
                    "& .MuiInputAdornment-root.MuiInputAdornment-positionStart": {
                        marginLeft: '-4px'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        // borderColor: '#8F9294',
                    },
                    "& .MuiInputBase-root": {
                        fontWeight: '500',
                        borderRadius: '6px',
                        backgroundColor: 'white',
                        // overflow: 'hidden',
                        '& fieldset': {
                            // marginTop: '1px'
                        },
                        '&:hover': {
                            '& fieldset': {
                                borderColor: theme.palette.focus.main
                            }
                        },
                        '&.Mui-focused': {
                            '&.Mui-error': {
                                '& fieldset': {
                                    borderColor: color.error
                                }
                            },
                            '& fieldset': {
                                borderColor: theme.palette.focus.main,
                                borderWidth: '1px'
                            }
                        },
                        '& input::placeholder': {
                            fontSize: '1rem',
                            color: color.placeHolder,
                            opacity: '1'
                        },
                        '&.Mui-error': {
                            '& fieldset': {
                                borderColor: color.error
                            }
                        },
                        '&:has(.MuiButtonBase-root)': {
                            paddingRight: 0,

                            '& .MuiButtonBase-root': {
                                padding: 0,
                                marginRight: '12px'
                            }
                        }
                    },
                    '& .MuiFormHelperText-root.Mui-error': {
                        color: color.error,
                        fontSize: '0.875rem',
                        lineHeight: '21px'
                    }
                })
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '& .MuiInputBase-root': {
                        borderRadius: '6px'
                    },
                    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                        border: 0
                    },
                    '&.Mui-disabled': {
                        backgroundColor: theme.palette.ui.disabled,
                        color: '#8F9294'
                    },
                    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                        borderColor: color.error,
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: theme.palette.focus.main,
                    }
                })
            }
        },
        MuiButtonBase: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '&.MuiPickersDay-root': {
                        borderRadius: '0.25em',
                        fontWeight: '600',
                        '&:hover': {
                            backgroundColor: theme.palette.ui.hover
                        },
                        '&.Mui-selected': {
                            fontWeight: '600',
                            color: '#fff',
                            backgroundColor: theme.palette.primary.main,
                            transition: 'auto',
                            '&:hover': {
                                backgroundColor: theme.palette.ui.hover
                            }
                        },
                        '&.MuiPickersDay-today': {
                            backgroundColor: '#FCE5EA',
                            border: 0,
                            position: 'relative',
                            color: theme.palette.primary.main,
                            '&:hover': {
                                backgroundColor: theme.palette.ui.hover
                            },
                            '&::after': {
                                content: '""',
                                display: 'block',
                                width: 0,
                                height: 0,
                                borderLeft: '10px solid transparent', /* Độ rộng của tam giác */
                                borderTop: '10px solid transparent', /* Độ rộng của tam giác */
                                borderBottom: '10px solid #ee0033', /* Màu và chiều cao của tam giác */
                                position: 'absolute',
                                bottom: '2px',
                                right: '2px'
                            }
                        }
                    }
                })
            }
        },
        MuiPickersYear: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '& .MuiPickersYear-yearButton.Mui-selected': {
                        borderRadius: '0.25em',
                        backgroundColor: theme.palette.primary.main
                    }
                })
            }
        },
        MuiTypography: {
            styleOverrides: {
                root: ({ theme }) => ({
                    color: '#000000',
                    '&.MuiDayCalendar-weekDayLabel': {
                        fontWeight: 600
                    },
                    '& .MuiDialogContentText-root': {
                        color: theme.palette.text.primary
                    }
                })
            }
        },
        MuiDialog: {
            styleOverrides: {
                root: {
                    '& .MuiPaper-root': {
                        maxWidth: 'unset',
                        boxShadow: '0px 0px 10px 4px #00000040',
                        borderRadius: '20px',
                        width: 'fit-content'
                    }
                }
            }
        },
        MuiDialogContent: {
            styleOverrides: {
                root: ({ theme }) => ({
                    [theme.breakpoints.down("sm")]: {
                        width: 'auto',
                        minWidth: '320px'
                    },
                    [theme.breakpoints.up("sm")]: {
                        minWidth: '400px',
                        maxWidth: '500px'
                    },
                    padding: '0.5em 2.5rem',
                })
            }
        },
        MuiDialogActions: {
            styleOverrides: {
                root: ({ theme }) => ({
                    [theme.breakpoints.down("sm")]: {
                        width: 'auto',
                        minWidth: '320px'
                    },
                    // borderTop: '1px solid #D1D1D1',
                    justifyContent: 'end',
                    padding: '0.5em 2.5rem 2.5rem 2.5rem'
                })
            }
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    // boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.1), 0px 2px 2px 0px rgba(0,0,0,0.1), 0px 1px 5px 0px rgba(0,0,0,0.1)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '1.5rem 2.5rem 0.25em 2.5rem',
                    fontWeight: 700,
                    fontSize: 24
                }
            }
        },
        MuiDialogContentText: {
            styleOverrides: {
                root: ({ theme}) => ({
                    color: theme.palette.text.primary,
                })
            }
        },
        MuiContainer: {
            styleOverrides: {
                root: {
                    padding: '0 !important'
                }
            }
        },
        MuiFormLabel: {
            styleOverrides: {
                root: ({ theme }) => ({
                    color: theme.palette.text.dark,
                    marginBottom: '0.375em',
                    lineHeight: '20px',
                    height: '20px',

                    '&.Mui-focused': {
                        color: theme.palette.text.dark
                    }
                })
            }
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    padding: '6px',
                }
            }
        },
        MuiSwitch: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '& .MuiSwitch-colorPrimary': {
                        '& .MuiSwitch-thumb': {
                            backgroundColor: '#fff',
                            border: '1px solid #949494'
                        }
                    },
                    '& .Mui-checked': {
                        '& .MuiSwitch-thumb': {
                            backgroundColor: theme.palette.primary.main,
                            border: '1px solid #fff'
                        }
                    }
                })
            }
        },
        MuiSelect: {
            styleOverrides: {
                root: ({ theme }) => ({
                    fontWeight: '500',
                    "& > div": {
                        padding: '6.5px 14px'
                    },
                    '& fieldset': {
                        borderColor: '#8F9294'
                    },
                    '&:hover': {
                        '& fieldset': {
                            borderColor: theme.palette.focus.main
                        }
                    },
                    '&.Mui-focused': {
                        '&.Mui-error': {
                            '& fieldset': {
                                borderColor: color.error
                            }
                        },
                        '& fieldset': {
                            borderColor: `${theme.palette.focus.main} !important`,
                        }
                    }
                })
            }
        },
        MuiMenuItem: {
            styleOverrides: {
                root: ({ theme }) => ({
                    "&:hover": {
                        color: theme.palette.primary.main,
                        backgroundColor: '#F0F0F0',
                        "& label": {
                            "& span:nth-child(2)": {
                                color: theme.palette.primary.main
                            }
                        }
                    },
                    "& .MuiFormControlLabel-root": {
                        "& .MuiTypography-root": {
                            "&:hover": {
                                color: theme.palette.primary.main,
                                backgroundColor: '#F0F0F0'
                            },
                        }
                    }
                })
            }
        },
        MuiPopper: {
            styleOverrides: {
                root: ({ theme }) => ({
                    "& .MuiAutocomplete-option.Mui-focused": {
                        color: theme.palette.primary.main
                    }
                })
            }
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    '& .MuiFormHelperText-root': {
                        marginLeft: '0px'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        // borderColor: '#8F9294'
                    }
                }
            }
        },
        MuiAutocomplete: {
            styleOverrides: {
                root: {
                    '& .MuiInputBase-root.MuiOutlinedInput-root': {
                        paddingTop: '4px',
                        paddingBottom: '4px'
                    }
                }
            }
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    '& span': {
                        padding: '0'
                    }
                }
            }
        },
        MuiFormControlLabel: {
            styleOverrides: {
                root: ({ theme }) => ({
                    rowGap: '0.125rem !important',
                    variants: [
                        {
                            props: { labelPlacement: 'top' },
                            style: {
                                margin: 0,
                                width: '100%',
                                alignItems: 'self-start',
                                rowGap: '0.5rem',
                            }
                        },
                        {
                            props: { labelPlacement: 'start' },
                            style: {
                                margin: 0,
                                width: '100%',
                                columnGap: '1.5rem',
                                justifyContent: 'flex-end',
                                '& .MuiStack-root': {
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'flex-end',
                                    flex: 1,
                                    alignSelf: 'flex-start',
                                    minWidth: '200px',

                                    '& .MuiFormControlLabel-label': {
                                        // lineHeight: '36px',
                                    }
                                },
                                '& .MuiFormControlLabel-label': {
                                    display: 'flex',
                                    flex: 1,
                                    justifyContent: 'flex-end',
                                    minWidth: '200px'
                                }
                            }
                        }
                    ],
                    '& .MuiFormControlLabel-label': {
                        fontSize: '1rem',
                        fontWeight: '500',
                        color: color.darkGray,

                        '&.Mui-disabled': {
                            color: color.darkGray,
                        }
                    },
                    '& .MuiFormControlLabel-asterisk': {
                        color: color.redP01
                    },
                    '& .textarea-auto-size': {
                        width: '100%',

                        '& .MuiFormHelperText-root.Mui-error': {
                            color: color.error,
                            fontSize: '0.875rem',
                            lineHeight: '21px',
                            // fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                            margin: 0
                        }
                    }
                })
            }
        },
        MuiGrid: {
            styleOverrides: {
                root: {

                }
            }
        },
        MuiInputBase: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '& input::placeholder': {
                        color: color.placeHolder,
                        opacity: '1'
                    },
                    '& textarea::placeholder': {
                        color: color.placeHolder,
                        opacity: '1'
                    },
                    '&.MuiOutlinedInput-root.Mui-focused fieldset.MuiOutlinedInput-notchedOutline': {
                        borderWidth: 1,
                    }
                })
            }
        }
    }
})

export default theme